import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Shield, Users, Package } from 'lucide-react';
import {
  permissionService,
  moduleAccessService,
  userGroupService,
  type UserProfileRow,
  type UserPermission,
  type UserModuleAccess,
  type UserGroupMembership,
} from '@/services';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface UserDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserProfileRow | null;
  companyId: string;
}

function getInitials(name: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return '\u2014';
  }
}

/* ---------- Source badge ---------- */

function SourceBadge({ source, groupName }: { source: 'DIRECT' | 'GROUP'; groupName: string | null }) {
  if (source === 'DIRECT') {
    return <Badge variant="secondary">Direct</Badge>;
  }
  return <Badge variant="outline">Group: {groupName}</Badge>;
}

/* ---------- Loading skeleton rows ---------- */

function SkeletonRows({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-20" />
        </div>
      ))}
    </div>
  );
}

/* ---------- Permissions tab ---------- */

function PermissionsTab({ permissions, isLoading }: { permissions: UserPermission[] | undefined; isLoading: boolean }) {
  const grouped = useMemo(() => {
    if (!permissions) return {};
    return permissions.reduce<Record<string, UserPermission[]>>((acc, p) => {
      const cat = p.category || 'Uncategorized';
      (acc[cat] ??= []).push(p);
      return acc;
    }, {});
  }, [permissions]);

  if (isLoading) return <SkeletonRows />;
  if (!permissions?.length) {
    return <p className="text-sm text-muted-foreground py-4">No permissions assigned</p>;
  }

  return (
    <div className="space-y-4">
      {Object.entries(grouped)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([category, perms]) => (
          <div key={category}>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              {category}
            </h4>
            <div className="space-y-2">
              {perms.map((p) => (
                <div key={`${p.permission_id}-${p.grant_source}-${p.group_id ?? ''}`} className="flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <span className="font-medium">{p.permission_name}</span>
                    <span className="ml-2 text-xs text-muted-foreground font-mono">{p.permission_code}</span>
                  </div>
                  <SourceBadge source={p.grant_source} groupName={p.group_name} />
                </div>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}

/* ---------- Groups tab ---------- */

function GroupsTab({ groups, isLoading }: { groups: UserGroupMembership[] | undefined; isLoading: boolean }) {
  if (isLoading) return <SkeletonRows />;
  if (!groups?.length) {
    return <p className="text-sm text-muted-foreground py-4">Not a member of any groups</p>;
  }

  return (
    <div className="space-y-2">
      {groups.map((g) => (
        <div key={g.group_id} className="flex items-center justify-between text-sm">
          <div className="min-w-0">
            <span className="font-medium">{g.group_name}</span>
            {g.group_description && (
              <p className="text-xs text-muted-foreground truncate">{g.group_description}</p>
            )}
          </div>
          <Badge variant={g.is_active ? 'secondary' : 'outline'}>
            {g.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      ))}
    </div>
  );
}

/* ---------- Modules tab ---------- */

function ModulesTab({ modules, isLoading }: { modules: UserModuleAccess[] | undefined; isLoading: boolean }) {
  if (isLoading) return <SkeletonRows />;
  if (!modules?.length) {
    return <p className="text-sm text-muted-foreground py-4">No module access assigned</p>;
  }

  return (
    <div className="space-y-2">
      {modules.map((m) => (
        <div key={`${m.licensed_module_id}-${m.access_source}-${m.group_id ?? ''}`} className="flex items-center justify-between text-sm">
          <div className="min-w-0">
            <span className="font-medium">{m.module_name}</span>
            <span className="ml-2 text-xs text-muted-foreground font-mono">{m.module_code}</span>
          </div>
          <SourceBadge source={m.access_source} groupName={m.group_name} />
        </div>
      ))}
    </div>
  );
}

/* ---------- Main dialog ---------- */

export default function UserDetailDialog({ open, onOpenChange, user, companyId }: UserDetailDialogProps) {
  const navigate = useNavigate();
  const enabled = open && !!user;

  const { data: permissions, isLoading: permLoading } = useQuery({
    queryKey: ['user-permissions', user?.id, companyId],
    queryFn: async () => {
      const r = await permissionService.getUserPermissions(user!.id, companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    enabled,
    staleTime: 2 * 60 * 1000,
  });

  const { data: groups, isLoading: groupsLoading } = useQuery({
    queryKey: ['user-groups', user?.id, companyId],
    queryFn: async () => {
      const r = await userGroupService.getUserGroups(user!.id, companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    enabled,
    staleTime: 2 * 60 * 1000,
  });

  const { data: modules, isLoading: modulesLoading } = useQuery({
    queryKey: ['user-modules', user?.id, companyId],
    queryFn: async () => {
      const r = await moduleAccessService.getUserModules(user!.id, companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    enabled,
    staleTime: 2 * 60 * 1000,
  });

  const { data: signedAvatarUrl } = useQuery({
    queryKey: ['avatar-url', user?.id, user?.avatar_url],
    queryFn: async () => {
      const { data } = await supabase.storage
        .from('avatars')
        .createSignedUrl(user!.avatar_url!, 3600);
      return data?.signedUrl ?? null;
    },
    enabled: !!user?.avatar_url,
    staleTime: 30 * 60 * 1000,
  });

  if (!user) return null;

  const isDeleted = !!user.deleted_at;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-4">
            <Avatar className="h-14 w-14">
              {signedAvatarUrl && <AvatarImage src={signedAvatarUrl} alt={user.full_name ?? ''} />}
              <AvatarFallback className="text-lg bg-primary/10 text-primary">
                {getInitials(user.full_name)}
              </AvatarFallback>
            </Avatar>

            <div className="min-w-0 flex-1">
              <DialogTitle className="text-lg flex items-center gap-2">
                {user.full_name || 'Unnamed User'}
                {isDeleted && <Badge variant="destructive">Deleted</Badge>}
              </DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-0.5 mt-1">
                  {user.email && <p className="text-sm">{user.email}</p>}
                  {user.phone && <p className="text-sm">{user.phone}</p>}
                  <p className="text-xs text-muted-foreground">
                    Last login: {formatDateTime(user.last_sign_in_at)}
                  </p>
                </div>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="permissions" className="mt-4">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="permissions" className="gap-1.5">
              <Shield className="h-3.5 w-3.5" />
              Permissions{permissions ? ` (${permissions.length})` : ''}
            </TabsTrigger>
            <TabsTrigger value="groups" className="gap-1.5">
              <Users className="h-3.5 w-3.5" />
              Groups{groups ? ` (${groups.length})` : ''}
            </TabsTrigger>
            <TabsTrigger value="modules" className="gap-1.5">
              <Package className="h-3.5 w-3.5" />
              Modules{modules ? ` (${modules.length})` : ''}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="permissions" className="mt-4">
            <PermissionsTab permissions={permissions} isLoading={permLoading} />
          </TabsContent>

          <TabsContent value="groups" className="mt-4">
            <GroupsTab groups={groups} isLoading={groupsLoading} />
          </TabsContent>

          <TabsContent value="modules" className="mt-4">
            <ModulesTab modules={modules} isLoading={modulesLoading} />
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              navigate(`/security/users/${user.id}`, { state: { user } });
            }}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            View Full Profile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
